<script setup lang="ts">
/**
 * This component shows the Gesuch-Adressen. It depends on Gesuch.config.kontakte,
 * which contains the display configuration for this view.
 */
import GesuchAdresseCard from '@/components/gesuch-adresse-card.vue'
import { KontaktConfig, SICHTBARKEIT } from '@/models/apollon_conf_fk/KontaktConfig'
import { Data } from '@/models/apollon_data/Data'
import { Gesuch, GesuchPhase } from '@/models/Gesuch'
import type { StepType } from './GesuchPanel.vue'
import { useRoute } from 'vuetify/lib/composables/router.mjs'

const {
    readOnly = false,
    gesuch,
    loading = false,
} = defineProps<{
    readOnly?: boolean
    gesuch: Gesuch
    loading?: boolean
}>()

function isVisible(gesuch: Gesuch, kontaktConfig: KontaktConfig | null | undefined) {
    if (kontaktConfig) {
        if (gesuch.phase === GesuchPhase.EINREICHUNG) {
            return (
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.EINREICHUNG ||
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.IMMER
            )
        } else if (gesuch.phase === GesuchPhase.ABSCHLUSS) {
            return (
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.ABSCHLUSS ||
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.IMMER
            )
        } else if (gesuch.phase === GesuchPhase.FERTIG) {
            return (
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.EINREICHUNG ||
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.ABSCHLUSS ||
                kontaktConfig.sichtbarkeit === SICHTBARKEIT.IMMER
            )
        }
    }
    return false
}

const emit = defineEmits<{
    (e: 'gesuchdatachanged', data: Data | null): void
    (e: 'next', type: StepType | null): void
}>()

function onGesuchDataChanged(data: Data | null) {
    // just inform the parent container about the change:
    emit('gesuchdatachanged', data)
}

const route = useRoute()
</script>

<template>
    <v-container fluid class="kp-large-container">
        <v-alert
            v-if="gesuch.config?.config?.kontakte?.infotext"
            type="info"
            :icon="false"
            border="start"
            colored-border
            variant="outlined"
            elevation="2"
        >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-html="gesuch.config?.config?.kontakte?.infotext.replace('\n', '<br />')">
            </span>
        </v-alert>
        <v-row>
            <v-col
                v-if="isVisible(gesuch, gesuch.config?.config?.kontakte?.eigentuemer)"
                cols="12"
                sm="6"
                md="4"
            >
                <GesuchAdresseCard
                    :gesuch="gesuch"
                    :read-only="readOnly"
                    kontakt-ident="eigentuemer"
                    :edit="route?.params.edit_type === 'eigentuemer'"
                    @gesuchdatachanged="onGesuchDataChanged"
                />
            </v-col>

            <v-col
                v-if="isVisible(gesuch, gesuch.config?.config?.kontakte?.unternehmer)"
                cols="12"
                sm="6"
                md="4"
            >
                <GesuchAdresseCard
                    :gesuch="gesuch"
                    :read-only="readOnly"
                    kontakt-ident="unternehmer"
                    :edit="route?.params.edit_type === 'unternehmer'"
                    @gesuchdatachanged="onGesuchDataChanged"
                />
            </v-col>
            <v-col
                v-if="isVisible(gesuch, gesuch.config?.config?.kontakte?.kontakt)"
                cols="12"
                sm="6"
                md="4"
            >
                <GesuchAdresseCard
                    :gesuch="gesuch"
                    :read-only="readOnly"
                    kontakt-ident="kontakt"
                    :edit="route?.params.edit_type === 'kontakt'"
                    @gesuchdatachanged="onGesuchDataChanged"
                />
            </v-col>
            <v-col
                v-if="isVisible(gesuch, gesuch.config?.config?.kontakte?.bevollmaechtigter)"
                cols="12"
                sm="6"
                md="4"
            >
                <GesuchAdresseCard
                    :gesuch="gesuch"
                    :read-only="readOnly"
                    kontakt-ident="bevollmaechtigter"
                    :edit="route?.params.edit_type === 'bevollmaechtigter'"
                    @gesuchdatachanged="onGesuchDataChanged"
                />
            </v-col>
        </v-row>
        <v-card-actions>
            <v-spacer />
            <v-btn
                variant="flat"
                class="mt-2"
                color="action"
                :loading="loading"
                @click="$emit('next', null)"
                append-icon="fas fa-arrow-right"
            >
                {{ $t('next_step') }}
            </v-btn>
        </v-card-actions>
    </v-container>
</template>
